import React, { useState, useEffect, useMemo } from 'react';
import { isNil, toString, isObject, toNumber } from 'lodash-es';
import {
  SharedDataTable,
  TableHeader,
  FormattedCell,
} from './shared-data-table';
import { useAtomValue } from 'jotai';
import { useTitleAssetsAtom } from 'pages/development-areas/show/atoms';
import {
  NUMERIC_FIELDS,
  createUnitInterestCell,
  getUnitNriFromAllocationMethod,
} from './shared-data-table-utils';
import { InlineNotification } from '@carbon/react';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import styles from './shared-data-table.module.scss';
import {
  nriBannerNotification,
  shouldShowNriNotification,
} from './warnings/nri-banner-notification';
import { ASSET_TYPES } from './constants';

interface TitleAssetsTableProps {
  activeAllocationMethod: 'acreage' | 'lateral_length';
  allocationManagerId?: number | null;
  initialColumnFilter?: { column: string; value: string }[];
  developmentAreaId: number;
  royaltyRate?: string;
  onAssetSelect?: (
    assetGeojson: any | null,
    intersectionGeojson?: any | null,
    tractGeojson?: any | null
  ) => void;
  onAssetsCollected?: (assetGeojsons: any[]) => void;
  onWarningStateChange?: (hasWarning: boolean) => void;
  overlapThreshold: number;
  onThresholdChange: (threshold: number) => void;
  onThresholdReset: () => void;
  participationFactorThreshold: number | null;
  onParticipationFactorThresholdChange: (threshold: number | null) => void;
}

const TITLE_ASSET_HEADERS: TableHeader[] = [
  { key: 'entityName', header: 'Entity' },
  { key: 'workspaceName', header: 'Workspace' },
  { key: 'unitNri', header: 'Unit NRI' },
  { key: 'unitWi', header: 'Unit WI' },
  {
    key: 'uat',
    header: 'UAT',
    meta: {
      filterVariant: 'dropdown',
    },
  },
  {
    key: 'participationTractAc',
    header: 'Part. Tract Acres',
  },
  { key: 'participationFactor', header: 'Part. Tract Factor' },
  { key: 'subtractName', header: 'Subtract Name' },
  { key: 'interestType', header: 'Interest Type' },
  { key: 'surface', header: 'Surface' },
  { key: 'startDepth', header: 'Start Depth (ft)' },
  { key: 'endDepth', header: 'End Depth (ft)' },
  { key: 'executive', header: 'Executive' },
  { key: 'bonus', header: 'Bonus' },
  { key: 'delay', header: 'Delay' },
  { key: 'royalty', header: 'Royalty' },
  { key: 'fixed', header: 'Fixed Royalty' },
  { key: 'wi', header: 'WI' },
  { key: 'orr', header: 'ORR' },
  { key: 'subtractNri', header: 'Subtract NRI' },
  { key: 'grossAcreage', header: 'GIS Gross Acreage' },
  { key: 'legalAcreage', header: 'Legal Acreage' },
  { key: 'lastCalculated', header: 'Last Calculated' },
  { key: 'royaltyRate', header: 'Royalty Rate' },
  { key: 'lease', header: 'Lease' },
  { key: 'burdenedEntityName', header: 'Burdened Entity' },
  { key: 'warnings', header: 'Warnings' },
];

const formatCellWithHandlers = (
  key: string,
  value: any, // cell value or cell content
  activeAllocationMethod: 'acreage' | 'lateral_length',
  allocationManagerId: number | null | undefined,
  row: any
): string | FormattedCell | React.ReactNode | null => {
  if (key === 'unitNri') {
    if (isNil(value)) {
      return null;
    }

    return {
      content: createUnitInterestCell(
        value,
        activeAllocationMethod,
        !!allocationManagerId,
        'Unit NRI Calculation',
        row.participationFactorOverridden
      ),
    };
  }

  if (key === 'unitWi') {
    if (isNil(value)) {
      return null;
    }

    return {
      content: createUnitInterestCell(
        value,
        activeAllocationMethod,
        !!allocationManagerId,
        'Unit WI Calculation',
        row.participationFactorOverridden
      ),
    };
  }

  if (key === 'royaltyRate') {
    if (isNil(value) || value === 0) {
      return <span className={styles.warningText}>--</span>;
    }
    if (typeof value === 'string') {
      return value;
    }
    return formatNumberWithTooltip(value, 8, '0.00000000', false);
  }

  if (key === 'startDepth' || key === 'endDepth') {
    return value === Number.POSITIVE_INFINITY
      ? '∞'
      : isNil(value)
        ? null
        : formatNumberWithTooltip(value, 2, '0.00000000', false);
  }

  if (key === 'interestType') {
    return toString(row.type || '');
  }

  if (NUMERIC_FIELDS.includes(key)) {
    return formatNumberWithTooltip(value, 8, '0.00000000', false);
  }

  if (key === 'entityName' && isObject(value)) {
    return 'entityId' in value && value.entityId !== null ? (
      <a href={`/entities/${value.entityId}`}>
        {'entityName' in value ? String(value.entityName || '') : ''}
      </a>
    ) : 'entityName' in value ? (
      String(value.entityName || '')
    ) : (
      ''
    );
  }

  if (key === 'workspaceName' && isObject(value)) {
    return 'workspaceId' in value && value.workspaceId !== null ? (
      <a href={`/title_workspaces/${value.workspaceId}`}>
        {'workspaceName' in value ? String(value.workspaceName || '') : ''}
      </a>
    ) : 'workspaceName' in value ? (
      String(value.workspaceName || '')
    ) : (
      ''
    );
  }

  return toString(value || '');
};

export const TitleAssetsTable: React.FC<TitleAssetsTableProps> = ({
  allocationManagerId,
  activeAllocationMethod,
  initialColumnFilter,
  developmentAreaId,
  onAssetSelect,
  onAssetsCollected,
  onWarningStateChange,
  overlapThreshold = 3,
  onThresholdChange,
  onThresholdReset,
  participationFactorThreshold,
  onParticipationFactorThresholdChange,
}) => {
  // State for the selected asset ID to highlight the row
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  const titleAssetsAtom = useTitleAssetsAtom(
    allocationManagerId ?? null,
    developmentAreaId,
    overlapThreshold,
    participationFactorThreshold
  );
  const titleAssetsResult = useAtomValue(titleAssetsAtom);

  const assetsData =
    titleAssetsResult &&
    'data' in titleAssetsResult &&
    titleAssetsResult.data &&
    !('error' in titleAssetsResult.data)
      ? titleAssetsResult.data
      : [];

  const isLoading =
    titleAssetsResult &&
    'loading' in titleAssetsResult &&
    titleAssetsResult.loading;

  const filterString = initialColumnFilter
    ? JSON.stringify(initialColumnFilter)
    : '';

  const memoizedColumnFilters = useMemo(() => {
    return initialColumnFilter
      ? initialColumnFilter.map((filter) => ({
          id: filter.column,
          value: filter.value,
        }))
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterString]);

  useEffect(() => {
    if (!isLoading && assetsData.length > 0 && onAssetsCollected) {
      const assetGeojsons = assetsData
        .filter((asset) => asset.assetGeojson)
        .map((asset) => asset.assetGeojson);

      const uniqueAssetStrings = new Set(
        assetGeojsons.map((geojson) => JSON.stringify(geojson))
      );
      const uniqueAssets = Array.from(uniqueAssetStrings).map((str) =>
        JSON.parse(str)
      );

      if (uniqueAssets.length > 0) {
        onAssetsCollected(uniqueAssets);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const hasWarning = shouldShowNriNotification(assetsData, ASSET_TYPES.TITLE);

  useEffect(() => {
    onWarningStateChange?.(hasWarning);
  }, [hasWarning, onWarningStateChange]);

  const nriNotification = nriBannerNotification(hasWarning);

  const formattedRows = assetsData.map((row) => {
    const formula =
      activeAllocationMethod === 'acreage'
        ? row.acreageUnitNriFormula
        : row.lateralUnitNriFormula;

    const nriValue = getUnitNriFromAllocationMethod(
      activeAllocationMethod,
      row
    );

    const subtractNriValue = formula?.tractNri;

    return {
      ...row,
      id: row.id || `title-asset-${row.subtractName}-${row.uat}`,
      entityName: {
        entityId: row.entityId,
        entityName: row.entityName,
      },
      workspaceName: {
        workspaceId: row.workspaceId,
        workspaceName: row.workspaceName,
      },
      subtractNri: subtractNriValue,
      interestType: row.type,
      unitNri: {
        value: nriValue,
        formula: formula,
        calculationMethod: 'nra',
        tractNriFormula: null,
        activeAllocationMethod: activeAllocationMethod,
        lateralUnitInterestFormula: row.lateralUnitNriFormula,
        acreageUnitInterestFormula: row.acreageUnitNriFormula,
      },
      unitWi: {
        value:
          activeAllocationMethod === 'acreage'
            ? row.acreageUnitWi
            : row.lateralUnitWi,
        formula:
          activeAllocationMethod === 'acreage'
            ? row.acreageUnitWiFormula
            : row.lateralUnitWiFormula,
        calculationMethod: 'wi',
        tractNriFormula: null,
        activeAllocationMethod: activeAllocationMethod,
        lateralUnitInterestFormula: row.lateralUnitWiFormula,
        acreageUnitInterestFormula: row.acreageUnitWiFormula,
      },
      surface: row.surface ?? 0.0,
      startDepth: row.startDepth ?? 0.0,
      endDepth: row.endDepth,
      warnings: row.warnings || '',
    };
  });

  const cellFormatterWithActiveMethod = (
    key: string,
    value: any,
    row: any
  ): string | FormattedCell | React.ReactNode | null => {
    return formatCellWithHandlers(
      key,
      value,
      activeAllocationMethod,
      allocationManagerId,
      row
    );
  };

  const getSubtotalRow = (
    tableRows: any[]
  ): Record<string, React.ReactNode> => {
    if (!tableRows || tableRows.length === 0) {
      return {};
    }

    const unitNriTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;

      const unitNri = row.original.unitNri;
      const value =
        typeof unitNri === 'object' && unitNri !== null
          ? toNumber(unitNri.value ?? 0)
          : 0;
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    const unitWiTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;

      const unitWi = row.original.unitWi;
      const value =
        typeof unitWi === 'object' && unitWi !== null
          ? toNumber(unitWi.value ?? 0)
          : 0;
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    const subtractNriTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;

      const value = toNumber(row.original.subtractNri ?? 0);
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    const surfaceTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;
      const value = toNumber(row.original.surface ?? 0);
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    const executiveTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;
      const value = toNumber(row.original.executive ?? 0);
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    const bonusTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;
      const value = toNumber(row.original.bonus ?? 0);
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    const delayTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;
      const value = toNumber(row.original.delay ?? 0);
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    const royaltyTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;
      const value = toNumber(row.original.royalty ?? 0);
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    const fixedTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;
      const value = toNumber(row.original.fixed ?? 0);
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    const wiTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;
      const value = toNumber(row.original.wi ?? 0);
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    const orrTotal = tableRows.reduce((acc, row) => {
      if (!row?.original) return acc;
      const value = toNumber(row.original.orr ?? 0);
      return acc + (isNil(value) ? 0 : value);
    }, 0);

    return {
      unitNri: formatNumberWithTooltip(unitNriTotal, 8, '0.00000000', false),
      unitWi: formatNumberWithTooltip(unitWiTotal, 8, '0.00000000', false),
      surface: formatNumberWithTooltip(surfaceTotal, 8, '0.00000000', false),
      executive: formatNumberWithTooltip(
        executiveTotal,
        8,
        '0.00000000',
        false
      ),
      bonus: formatNumberWithTooltip(bonusTotal, 8, '0.00000000', false),
      delay: formatNumberWithTooltip(delayTotal, 8, '0.00000000', false),
      royalty: formatNumberWithTooltip(royaltyTotal, 8, '0.00000000', false),
      fixed: formatNumberWithTooltip(fixedTotal, 8, '0.00000000', false),
      wi: formatNumberWithTooltip(wiTotal, 8, '0.00000000', false),
      orr: formatNumberWithTooltip(orrTotal, 8, '0.00000000', false),
      subtractNri: formatNumberWithTooltip(
        subtractNriTotal,
        8,
        '0.00000000',
        false
      ),
    };
  };

  if (formattedRows.length === 0 && !isLoading) {
    return (
      <InlineNotification
        data-testid="no-data-message"
        kind="warning"
        title="No Data Available"
        subtitle="There are currently no Title assets for this unit. Contact Support for assistance."
        hideCloseButton
      />
    );
  }

  const handleShowOnMapClick = (asset: any) => {
    if (selectedAssetId === asset.id) {
      setSelectedAssetId(null);
      if (onAssetSelect) {
        onAssetSelect(null, null, null);
      }
      return;
    }

    setSelectedAssetId(asset.id);

    if (onAssetSelect && asset.assetGeojson) {
      onAssetSelect(
        asset.assetGeojson,
        asset.intersectionGeojson || null,
        asset.tractGeojson || null
      );
    }
  };

  const handleFilteredDataChange = (filteredData: any[]) => {
    if (!onAssetsCollected) return;

    try {
      if (!Array.isArray(filteredData) || filteredData.length === 0) {
        onAssetsCollected([]);
        return;
      }

      const uniqueAssets = Array.from(
        new Set(
          filteredData
            .filter((asset) => asset?.assetGeojson)
            .map((asset) => JSON.stringify(asset.assetGeojson))
        )
      ).map((str) => JSON.parse(str));

      onAssetsCollected(uniqueAssets);
    } catch (e) {
      console.error('Error processing filtered data', e);
      onAssetsCollected([]);
    }
  };

  return (
    <SharedDataTable
      headers={TITLE_ASSET_HEADERS}
      data={formattedRows}
      formatCellValue={cellFormatterWithActiveMethod}
      filename="title-assets"
      loading={isLoading ?? false}
      initialColumnFilters={memoizedColumnFilters}
      onRowClick={
        onAssetSelect ? (row) => handleShowOnMapClick(row) : undefined
      }
      selectedRowId={selectedAssetId}
      getSubtotalRow={getSubtotalRow}
      warningContent={nriNotification}
      onFilteredDataChange={handleFilteredDataChange}
      currentThreshold={overlapThreshold}
      onThresholdChange={onThresholdChange}
      onThresholdReset={onThresholdReset}
      participationFactorThreshold={participationFactorThreshold}
      onParticipationFactorThresholdChange={
        onParticipationFactorThresholdChange
      }
    />
  );
};
